import { useState } from "react";
import { useForm, useStore } from "@tanstack/react-form";
import { zodValidator } from "@tanstack/zod-form-adapter";
import * as z from "zod";
import { ChevronDown, ChevronRight, SlidersHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  TanstackFieldWrapper,
  TanstackFormControl,
} from "@/components/ui/tanstack-form";
import { NumericInput } from "@/components/ui/numeric-input";
import { ComboboxConfigMaster } from "@/components/shared/ComboboxConfigMaster";
import { focusFirstError } from "@/lib/form/formUtils";
import {
  generateProcessStepCode,
  getDefaultMachineTypeForStage,
  isMachineTypeCompatibleWithStage,
} from "./process-stage-machines.helper";

const processSchema = z
  .object({
    stepId: z.string().optional().default(""),
    name: z.string().min(2, "Tên công đoạn phải có ít nhất 2 ký tự"),
    group: z.string().nullable().optional(),
    description: z.string().nullable().optional(),
    defaultMachineType: z
      .string({
        required_error: "Vui lòng chọn loại máy mặc định",
      })
      .min(1, "Vui lòng chọn loại máy mặc định"),
    unit: z.string().min(1, "Vui lòng chọn đơn vị tính"),
    setupCost: z.coerce.number().min(0, "Chi phí setup phải >= 0"),
    unitCost: z.coerce.number().min(0, "Chi phí đơn vị phải >= 0"),
    speed: z.coerce.number().min(1, "Tốc độ định mức phải lớn hơn 0"),
    productionTime: z.coerce.number().min(0, "Thời gian sản xuất phải >= 0"),
    wastePercent: z.coerce.number().min(0, "% Hao phí phải >= 0"),
    minWasteQty: z.coerce.number().min(0, "Hao phí tối thiểu phải >= 0"),
    defaultSetupTime: z.coerce.number().min(0, "Thời gian setup chuẩn phải >= 0").optional(),
    defaultSpeed: z.coerce.number().min(1, "Tốc độ chuẩn phải >= 1").optional(),
    defaultSetupWaste: z.coerce.number().min(0, "Hao phí setup phải >= 0").optional(),
    defaultRunWastePct: z.coerce.number().min(0, "% Hao phí chạy phải >= 0").optional(),
    changeoverTime: z.coerce.number().min(0).optional(),
    washTime: z.coerce.number().min(0).optional(),
    warmupTime: z.coerce.number().min(0).optional(),
    sortOrder: z.coerce.number().default(0),
    cmyk: z.boolean().default(false),
    pantone: z.boolean().default(false),
    requiresDie: z.boolean().default(false),
  })
  .superRefine((val, ctx) => {
    const resolvedCode =
      (val.stepId || "").trim() || generateProcessStepCode(val.name, val.group);
    if (resolvedCode.length < 2) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["stepId"],
        message: "Mã công đoạn phải có ít nhất 2 ký tự",
      });
    }
  });

type ProcessFormValues = z.infer<typeof processSchema>;

interface ProcessFormProps {
  onSuccess: (data: ProcessFormValues) => void;
  onCancel: () => void;
  defaultValues?: Partial<ProcessFormValues>;
  /** Khi mở từ modal thêm nhanh trong Đơn hàng / Sản phẩm: tự sinh Mã công đoạn và thu gọn phần 2 định mức APS/MES */
  isQuickModal?: boolean;
  /** Khóa nhóm công đoạn khi mở đúng ngữ cảnh (VD: Nhóm Công Đoạn = In) */
  lockedGroup?: boolean;
}

export function ProcessForm({
  onSuccess,
  onCancel,
  defaultValues,
  isQuickModal = false,
  lockedGroup = false,
}: ProcessFormProps) {
  const [isAdvancedOpen, setIsAdvancedOpen] = useState<boolean>(!isQuickModal);
  const [isStepCodeManual, setIsStepCodeManual] = useState<boolean>(
    Boolean(defaultValues?.stepId && !isQuickModal),
  );

  const initialGroup = defaultValues?.group || "";
  const initialStepId =
    defaultValues?.stepId ||
    (defaultValues?.name
      ? generateProcessStepCode(defaultValues.name, initialGroup)
      : "");
  const initialMachineType =
    defaultValues?.defaultMachineType ||
    getDefaultMachineTypeForStage(initialGroup);

  const form = useForm({
    defaultValues: {
      stepId: initialStepId,
      name: defaultValues?.name || "",
      group: initialGroup,
      description: defaultValues?.description || "",
      defaultMachineType: initialMachineType,
      unit: defaultValues?.unit || "tờ",
      setupCost: defaultValues?.setupCost || 0,
      unitCost: defaultValues?.unitCost || 0,
      speed: defaultValues?.speed || 5000,
      productionTime: defaultValues?.productionTime || 0,
      wastePercent: defaultValues?.wastePercent || 0,
      minWasteQty: defaultValues?.minWasteQty || 0,
      defaultSetupTime: (defaultValues as any)?.defaultSetupTime ?? 30,
      defaultSpeed: (defaultValues as any)?.defaultSpeed ?? defaultValues?.speed ?? 5000,
      defaultSetupWaste: (defaultValues as any)?.defaultSetupWaste ?? defaultValues?.minWasteQty ?? 30,
      defaultRunWastePct: (defaultValues as any)?.defaultRunWastePct ?? defaultValues?.wastePercent ?? 2.0,
      changeoverTime: (defaultValues as any)?.specs?.changeoverTime ?? 10,
      washTime: (defaultValues as any)?.specs?.washTime ?? 0,
      warmupTime: (defaultValues as any)?.specs?.warmupTime ?? 0,
      sortOrder: defaultValues?.sortOrder || 0,
      cmyk: defaultValues?.cmyk ?? initialGroup === "IN",
      pantone: defaultValues?.pantone ?? false,
      requiresDie: defaultValues?.requiresDie ?? false,
    },
    // @ts-ignore
    validatorAdapter: zodValidator(),
    validators: { onChange: processSchema as any },
    onSubmit: async ({ value }) => {
      const { changeoverTime, washTime, warmupTime, ...rest } = value as any;
      const resolvedStepId =
        String(rest.stepId || "").trim() ||
        generateProcessStepCode(rest.name, rest.group);
      const existingSpecs = (defaultValues as any)?.specs || {};
      const updatedSpecs = {
        ...existingSpecs,
        changeoverTime: changeoverTime !== undefined ? Number(changeoverTime) : existingSpecs.changeoverTime,
        washTime: washTime !== undefined ? Number(washTime) : existingSpecs.washTime,
        warmupTime: warmupTime !== undefined ? Number(warmupTime) : existingSpecs.warmupTime,
      };
      onSuccess({
        ...rest,
        stepId: resolvedStepId,
        defaultSetupTime: value.defaultSetupTime !== undefined ? Number(value.defaultSetupTime) : undefined,
        defaultSpeed: value.defaultSpeed !== undefined ? Number(value.defaultSpeed) : undefined,
        defaultSetupWaste: value.defaultSetupWaste !== undefined ? Number(value.defaultSetupWaste) : undefined,
        defaultRunWastePct: value.defaultRunWastePct !== undefined ? Number(value.defaultRunWastePct) : undefined,
        specs: updatedSpecs,
      } as any);
    },
  });

  const currentGroup = useStore(
    form.store,
    (state: any) => state.values.group || "",
  );
  const currentStepId = useStore(
    form.store,
    (state: any) => state.values.stepId || "",
  );

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
        requestAnimationFrame(() => focusFirstError(form.state as any));
      }}
      className="space-y-5"
    >
      {/* 1. THÔNG TIN CHUNG */}
      <Card className="border-none shadow-none p-0">
        <CardHeader className="p-0 pb-3">
          <CardTitle className="text-sm font-semibold uppercase text-muted-foreground flex items-center justify-between">
            <span>1. Thông Tin Chung</span>
            {isQuickModal && currentStepId && (
              <span
                className="text-[11px] font-mono font-normal text-slate-500 bg-slate-100 px-2 py-0.5 rounded border border-slate-200 normal-case"
                data-testid="process-auto-step-code-badge"
              >
                Mã tự sinh: <strong className="text-slate-700">{currentStepId}</strong>
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0 space-y-4">
          <form.Field name="name">
            {(field: any) => (
              <TanstackFieldWrapper field={field} label="Tên Công Đoạn" required>
                <TanstackFormControl>
                  <Input
                    data-testid="process-name-input"
                    placeholder="VD: In Offset 4 màu, Phủ UV cục bộ..."
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => {
                      const nextName = e.target.value;
                      field.handleChange(nextName);
                      if (!isStepCodeManual) {
                        const groupVal = form.getFieldValue("group") || "";
                        form.setFieldValue(
                          "stepId",
                          generateProcessStepCode(nextName, groupVal),
                        );
                      }
                    }}
                  />
                </TanstackFormControl>
              </TanstackFieldWrapper>
            )}
          </form.Field>

          <div
            className={
              isQuickModal
                ? "grid grid-cols-1 sm:grid-cols-3 gap-4"
                : "grid grid-cols-1 sm:grid-cols-2 gap-4"
            }
          >
            {!isQuickModal && (
              <form.Field name="stepId">
                {(field: any) => (
                  <TanstackFieldWrapper field={field} label="Mã Công Đoạn" required>
                    <TanstackFormControl>
                      <Input
                        placeholder="VD: IN-OFFSET-4-MAU"
                        value={field.state.value}
                        autoCorrect="off"
                        autoCapitalize="off"
                        spellCheck={false}
                        onChange={(e) => {
                          setIsStepCodeManual(Boolean(e.target.value.trim()));
                          field.handleChange(e.target.value);
                        }}
                        onBlur={field.handleBlur}
                      />
                    </TanstackFormControl>
                  </TanstackFieldWrapper>
                )}
              </form.Field>
            )}

            <form.Field name="group">
              {(field: any) => (
                <TanstackFieldWrapper field={field} label="Nhóm Công Đoạn">
                  <TanstackFormControl>
                    <ComboboxConfigMaster
                      categoryCode="PROCESS_STAGE"
                      value={field.state.value || ""}
                      disabled={lockedGroup}
                      onChange={(nextGroup) => {
                        field.handleChange(nextGroup);
                        const currentName = form.getFieldValue("name") || "";
                        if (!isStepCodeManual && currentName) {
                          form.setFieldValue(
                            "stepId",
                            generateProcessStepCode(currentName, nextGroup),
                          );
                        }
                        const currentMach =
                          form.getFieldValue("defaultMachineType") || "";
                        if (
                          !currentMach ||
                          !isMachineTypeCompatibleWithStage(
                            currentMach,
                            undefined,
                            nextGroup,
                          )
                        ) {
                          const stageDefault =
                            getDefaultMachineTypeForStage(nextGroup);
                          if (stageDefault) {
                            form.setFieldValue(
                              "defaultMachineType",
                              stageDefault,
                            );
                          }
                        }
                      }}
                      placeholder="Chọn nhóm..."
                    />
                  </TanstackFormControl>
                </TanstackFieldWrapper>
              )}
            </form.Field>

            <form.Field name="defaultMachineType">
              {(field: any) => (
                <TanstackFieldWrapper field={field} label="Loại Máy Mặc Định" required>
                  <TanstackFormControl>
                    <ComboboxConfigMaster
                      categoryCode="MACHINE_TYPE"
                      value={field.state.value || ""}
                      onChange={field.handleChange}
                      placeholder="Chọn loại máy..."
                      filterOptions={(opt) =>
                        isMachineTypeCompatibleWithStage(
                          opt.id,
                          opt.label,
                          currentGroup,
                        )
                      }
                    />
                  </TanstackFormControl>
                </TanstackFieldWrapper>
              )}
            </form.Field>

            <form.Field name="unit">
              {(field: any) => (
                <TanstackFieldWrapper field={field} label="Đơn Vị Tính" required>
                  <TanstackFormControl>
                    <ComboboxConfigMaster
                      categoryCode="UOM"
                      value={field.state.value || ""}
                      onChange={field.handleChange}
                      placeholder="Chọn đơn vị..."
                    />
                  </TanstackFormControl>
                </TanstackFieldWrapper>
              )}
            </form.Field>
          </div>
        </CardContent>
      </Card>

      <Separator />

      {/* 2. THÔNG SỐ KIỂM SOÁT ĐỊNH MỨC (THU GỌN MẶC ĐỊNH KHI THÊM NHANH TỪ ĐƠN HÀNG / SẢN PHẨM) */}
      <Collapsible open={isAdvancedOpen} onOpenChange={setIsAdvancedOpen}>
        <Card className="border-none shadow-none p-0">
          <CardHeader className="p-0 pb-2">
            <CollapsibleTrigger asChild>
              <button
                type="button"
                data-testid="process-advanced-norms-toggle"
                aria-expanded={isAdvancedOpen}
                className="w-full flex items-center justify-between text-left py-1.5 px-2.5 -mx-2.5 rounded-md hover:bg-slate-50 transition-colors cursor-pointer"
              >
                <span className="text-sm font-semibold uppercase text-muted-foreground flex items-center gap-2">
                  {isAdvancedOpen ? (
                    <ChevronDown className="h-4 w-4 text-slate-500" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-slate-500" />
                  )}
                  <SlidersHorizontal className="h-3.5 w-3.5 text-indigo-600" />
                  <span>2. Định Mức Kỹ Thuật & Kế Hoạch Sản Xuất</span>
                </span>
                <span className="text-[11px] font-medium text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded border border-indigo-200">
                  {isAdvancedOpen
                    ? "Thu gọn thông số kỹ thuật"
                    : "Dành cho Kỹ thuật & Kế hoạch SX (Bấm để mở)"}
                </span>
              </button>
            </CollapsibleTrigger>
          </CardHeader>
          <CollapsibleContent className="pt-2">
            <CardContent className="p-0 space-y-4">
              {isQuickModal && (
                <form.Field name="stepId">
                  {(field: any) => (
                    <TanstackFieldWrapper
                      field={field}
                      label="Mã Công Đoạn (Tự động sinh từ tên — chỉ sửa khi cần)"
                    >
                      <TanstackFormControl>
                        <Input
                          placeholder="Tự sinh theo tên công đoạn..."
                          value={field.state.value}
                          autoCorrect="off"
                          autoCapitalize="off"
                          spellCheck={false}
                          onChange={(e) => {
                            setIsStepCodeManual(Boolean(e.target.value.trim()));
                            field.handleChange(e.target.value);
                          }}
                          onBlur={field.handleBlur}
                        />
                      </TanstackFormControl>
                    </TanstackFieldWrapper>
                  )}
                </form.Field>
              )}
          {/* Nhóm định mức thời gian & tốc độ cốt lõi */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 border border-indigo-100 rounded-lg bg-indigo-50/30">
            <form.Field name="defaultSetupTime">
              {(field: any) => (
                <TanstackFieldWrapper
                  field={field}
                  label="TG Setup Chuẩn (Phút)"
                >
                  <TanstackFormControl>
                    <NumericInput
                      placeholder="VD: 45"
                      value={field.state.value}
                      onChange={(e) =>
                        field.handleChange(Number(e.target.value))
                      }
                      onBlur={field.handleBlur}
                    />
                  </TanstackFormControl>
                </TanstackFieldWrapper>
              )}
            </form.Field>
            <form.Field name="defaultSpeed">
              {(field: any) => (
                <TanstackFieldWrapper
                  field={field}
                  label="Tốc Độ Chuẩn (SL/Giờ)"
                  required
                >
                  <TanstackFormControl>
                    <NumericInput
                      placeholder="VD: 7000"
                      value={field.state.value}
                      onChange={(e) =>
                        field.handleChange(Number(e.target.value))
                      }
                      onBlur={field.handleBlur}
                    />
                  </TanstackFormControl>
                </TanstackFieldWrapper>
              )}
            </form.Field>
            <form.Field name="defaultSetupWaste">
              {(field: any) => (
                <TanstackFieldWrapper
                  field={field}
                  label="Hao Phí Setup (Tờ/Cái)"
                >
                  <TanstackFormControl>
                    <NumericInput
                      placeholder="VD: 100"
                      value={field.state.value}
                      onChange={(e) =>
                        field.handleChange(Number(e.target.value))
                      }
                      onBlur={field.handleBlur}
                    />
                  </TanstackFormControl>
                </TanstackFieldWrapper>
              )}
            </form.Field>
            <form.Field name="defaultRunWastePct">
              {(field: any) => (
                <TanstackFieldWrapper
                  field={field}
                  label="% Bù Hao Chạy (%)"
                >
                  <TanstackFormControl>
                    <NumericInput
                      step="0.1"
                      placeholder="VD: 2.5"
                      value={field.state.value}
                      onChange={(e) =>
                        field.handleChange(Number(e.target.value))
                      }
                      onBlur={field.handleBlur}
                    />
                  </TanstackFormControl>
                </TanstackFieldWrapper>
              )}
            </form.Field>
          </div>

          {/* Nhóm thông số vận hành ca máy & phụ trợ từ Excel */}
          <div className="grid grid-cols-3 gap-4 p-3 border border-slate-200 rounded-lg bg-slate-50/60">
            <form.Field name="changeoverTime">
              {(field: any) => (
                <TanstackFieldWrapper
                  field={field}
                  label="Chuyển bài cùng khuôn/dạng (phút)"
                >
                  <TanstackFormControl>
                    <NumericInput
                      placeholder="VD: 10"
                      value={field.state.value}
                      onChange={(e) =>
                        field.handleChange(Number(e.target.value))
                      }
                      onBlur={field.handleBlur}
                    />
                  </TanstackFormControl>
                </TanstackFieldWrapper>
              )}
            </form.Field>
            <form.Field name="washTime">
              {(field: any) => (
                <TanstackFieldWrapper
                  field={field}
                  label="Rửa máy / Đổi mực (phút)"
                >
                  <TanstackFormControl>
                    <NumericInput
                      placeholder="VD: 20"
                      value={field.state.value}
                      onChange={(e) =>
                        field.handleChange(Number(e.target.value))
                      }
                      onBlur={field.handleBlur}
                    />
                  </TanstackFormControl>
                </TanstackFieldWrapper>
              )}
            </form.Field>
            <form.Field name="warmupTime">
              {(field: any) => (
                <TanstackFieldWrapper
                  field={field}
                  label="Chờ nhiệt đầu ngày (phút)"
                >
                  <TanstackFormControl>
                    <NumericInput
                      placeholder="VD: 30"
                      value={field.state.value}
                      onChange={(e) =>
                        field.handleChange(Number(e.target.value))
                      }
                      onBlur={field.handleBlur}
                    />
                  </TanstackFormControl>
                </TanstackFieldWrapper>
              )}
            </form.Field>
          </div>

          {/* Chi phí & thông số định mức giá thành */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <form.Field name="setupCost">
              {(field: any) => (
                <TanstackFieldWrapper
                  field={field}
                  label="Chi Phí Setup (VNĐ)"
                >
                  <TanstackFormControl>
                    <NumericInput
                      value={field.state.value}
                      onChange={(e) =>
                        field.handleChange(Number(e.target.value))
                      }
                      onBlur={field.handleBlur}
                    />
                  </TanstackFormControl>
                </TanstackFieldWrapper>
              )}
            </form.Field>
            <form.Field name="unitCost">
              {(field: any) => (
                <TanstackFieldWrapper field={field} label="Chi Phí / Đơn vị (VNĐ)">
                  <TanstackFormControl>
                    <NumericInput
                      value={field.state.value}
                      onChange={(e) =>
                        field.handleChange(Number(e.target.value))
                      }
                      onBlur={field.handleBlur}
                    />
                  </TanstackFormControl>
                </TanstackFieldWrapper>
              )}
            </form.Field>
            <form.Field name="speed">
              {(field: any) => (
                <TanstackFieldWrapper field={field} label="Tốc độ thiết bị (SL/Giờ)">
                  <TanstackFormControl>
                    <NumericInput
                      value={field.state.value}
                      onChange={(e) =>
                        field.handleChange(Number(e.target.value))
                      }
                      onBlur={field.handleBlur}
                    />
                  </TanstackFormControl>
                </TanstackFieldWrapper>
              )}
            </form.Field>
            <form.Field name="productionTime">
              {(field: any) => (
                <TanstackFieldWrapper field={field} label="TG. SX TB (Phút/1000)">
                  <TanstackFormControl>
                    <NumericInput
                      value={field.state.value}
                      onChange={(e) =>
                        field.handleChange(Number(e.target.value))
                      }
                      onBlur={field.handleBlur}
                    />
                  </TanstackFormControl>
                </TanstackFieldWrapper>
              )}
            </form.Field>
            <form.Field name="wastePercent">
              {(field: any) => (
                <TanstackFieldWrapper field={field} label="% Hao Phí (Báo Giá)">
                  <TanstackFormControl>
                    <NumericInput
                      step="0.1"
                      value={field.state.value}
                      onChange={(e) =>
                        field.handleChange(Number(e.target.value))
                      }
                      onBlur={field.handleBlur}
                    />
                  </TanstackFormControl>
                </TanstackFieldWrapper>
              )}
            </form.Field>
            <form.Field name="minWasteQty">
              {(field: any) => (
                <TanstackFieldWrapper
                  field={field}
                  label="Hao Phí Tối Thiểu (Bù Hao)"
                >
                  <TanstackFormControl>
                    <NumericInput
                      value={field.state.value}
                      onChange={(e) =>
                        field.handleChange(Number(e.target.value))
                      }
                      onBlur={field.handleBlur}
                    />
                  </TanstackFormControl>
                </TanstackFieldWrapper>
              )}
            </form.Field>
          </div>

          {/* Yêu cầu đính kèm khuôn/bản in */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 border rounded-md bg-slate-50">
            <form.Field name="cmyk">
              {(field: any) => (
                <TanstackFieldWrapper
                  field={field}
                  className="flex flex-row items-start space-x-3 space-y-0 rounded-md"
                >
                  <TanstackFormControl>
                    <Checkbox
                      checked={field.state.value}
                      onCheckedChange={field.handleChange}
                      onBlur={field.handleBlur}
                    />
                  </TanstackFormControl>
                  <div className="space-y-1 leading-none">
                    <label className="text-sm font-medium leading-none">
                      Hỗ trợ in CMYK
                    </label>
                  </div>
                </TanstackFieldWrapper>
              )}
            </form.Field>
            <form.Field name="pantone">
              {(field: any) => (
                <TanstackFieldWrapper
                  field={field}
                  className="flex flex-row items-start space-x-3 space-y-0 rounded-md"
                >
                  <TanstackFormControl>
                    <Checkbox
                      checked={field.state.value}
                      onCheckedChange={field.handleChange}
                      onBlur={field.handleBlur}
                    />
                  </TanstackFormControl>
                  <div className="space-y-1 leading-none">
                    <label className="text-sm font-medium leading-none">
                      Hỗ trợ in Pantone
                    </label>
                  </div>
                </TanstackFieldWrapper>
              )}
            </form.Field>
            <form.Field name="requiresDie">
              {(field: any) => (
                <TanstackFieldWrapper
                  field={field}
                  className="flex flex-row items-start space-x-3 space-y-0 rounded-md"
                >
                  <TanstackFormControl>
                    <Checkbox
                      checked={field.state.value}
                      onCheckedChange={field.handleChange}
                      onBlur={field.handleBlur}
                    />
                  </TanstackFormControl>
                  <div className="space-y-1 leading-none">
                    <label className="text-sm font-medium leading-none">
                      Yêu Cầu Khuôn Bế / Khuôn Ép
                    </label>
                  </div>
                </TanstackFieldWrapper>
              )}
            </form.Field>
          </div>
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button variant="outline" type="button" onClick={onCancel}>
          Hủy
        </Button>
        <form.Subscribe
          selector={(state) => [state.canSubmit, state.isSubmitting]}
          children={([canSubmit, isSubmitting]: any) => (
            <Button
              type="submit"
              className="bg-indigo-600 hover:bg-indigo-700"
              disabled={!canSubmit || isSubmitting}
            >
              Lưu Công Đoạn
            </Button>
          )}
        />
      </div>
    </form>
  );
}
